import { useRef, type RefObject } from 'react';
import * as React from 'react';
import clsx from 'clsx';
import {
  useListBox,
  useOption,
  type AriaOptionProps,
  type AriaListBoxOptions,
} from 'react-aria';
import { type ListState, type Node } from 'react-stately';
import styles from './SelectListBox.module.css';

interface ListBoxProps<T extends object> extends AriaListBoxOptions<T> {
  testid?: string;
  state: ListState<T>;
  listBoxRef?: RefObject<HTMLUListElement>;
  highlightVariant?: 'neutral' | 'interactive';
}

interface OptionProps<T extends object> extends AriaOptionProps {
  testid?: string;
  state: ListState<T>;
  item: Node<T>;
  highlightVariant?: 'neutral' | 'interactive';
}

export function SelectListBox<T extends object>(props: ListBoxProps<T>) {
  const internalRef = useRef<HTMLUListElement>(null);
  const ref = props.listBoxRef ?? internalRef;
  const { testid, state, highlightVariant } = props;
  const { listBoxProps } = useListBox(props, state, ref);

  return (
    <ul {...listBoxProps} ref={ref}>
      {Array.from(state.collection).map((item, index) => (
        <SelectListBox.Option
          key={item.key}
          item={item}
          state={state}
          testid={`${testid}-${index}`}
          highlightVariant={highlightVariant}
        />
      ))}
    </ul>
  );
}

function SelectListBoxOption<T extends object>(props: OptionProps<T>) {
  const { testid, item, state, highlightVariant } = props;
  const ref = React.useRef(null);
  const { optionProps, isFocused, isSelected } = useOption(
    { key: item.key },
    state,
    ref
  );

  return (
    <li
      {...optionProps}
      data-testid={testid}
      ref={ref}
      className={clsx(styles.item, {
        [styles['is-active']]: isSelected,
        [styles['is-focused']]: isFocused,
        [styles['is-interactive-highlight']]:
          highlightVariant === 'interactive',
      })}
    >
      {item.rendered}
    </li>
  );
}

SelectListBox.Option = SelectListBoxOption;
